import { memo, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectorTypeEnum } from '@shared/enums/connector-type';
import { LoadBalancingStrategyEnum } from '../../../../shared/enums/load-balancing-strategy.enum';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { StationFormData } from '../stationForm';
import { NumberField } from './NumberField';
import { OrganizationSelect } from '../../../../components/common/OrganizationSelect/OrganizationSelect';

interface TechnicalSpecsSectionProps {
  formData: StationFormData;
  onFieldChange: <K extends keyof StationFormData>(
    field: K,
    value: StationFormData[K]
  ) => void;
  hideIdTag?: boolean;
}

const CONNECTOR_OPTIONS = Object.values(ConnectorTypeEnum);

export const TechnicalSpecsSection = memo(function TechnicalSpecsSection({
  formData,
  onFieldChange,
  hideIdTag = false,
}: TechnicalSpecsSectionProps) {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Organization select is admin-only; owners' own org is attached server-side.
  const { has, organization } = usePermissions();
  // Assigning a station to an organization is platform-admin only.
  const showOrganizationSelect = has('organizations.manage') && !organization;

  // client-event-listeners: Use shared hook for click-outside
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  useClickOutside(dropdownRef, closeDropdown, isDropdownOpen);

  const handleIdTagChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange('idTag', e.target.value);
    },
    [onFieldChange]
  );

  const handleConnectorToggle = useCallback(
    (connectorType: ConnectorTypeEnum) => {
      const currentTypes = formData.connectorTypes;
      const newTypes = currentTypes.includes(connectorType)
        ? currentTypes.filter((t) => t !== connectorType)
        : [...currentTypes, connectorType];
      onFieldChange('connectorTypes', newTypes);
    },
    [onFieldChange, formData.connectorTypes]
  );

  const handlePriceChange = useCallback(
    (value: number) => onFieldChange('price', value),
    [onFieldChange]
  );

  const handleOptionChange = useCallback(
    (value: number) => onFieldChange('option', value),
    [onFieldChange]
  );

  const handleCurrentLimitChange = useCallback(
    (value: number | undefined) => onFieldChange('currentLimit', value),
    [onFieldChange]
  );

  const handleLoadBalancingStrategyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFieldChange('loadBalancingStrategy', e.target.value as LoadBalancingStrategyEnum);
    },
    [onFieldChange]
  );

  const handleDowntimePriceChange = useCallback(
    (value: number) => onFieldChange('downtimePricePerMinute', value),
    [onFieldChange]
  );

  const handleDowntimeFreeChange = useCallback(
    (value: number) => onFieldChange('downtimeFreeMinutes', value),
    [onFieldChange]
  );

  return (
    <section className="form-section">
      <h2 className="section-title">
        <span className="material-symbols-outlined section-icon">bolt</span>
        Technical Specifications
      </h2>

      <div className="form-grid">
        {!hideIdTag && (
          <div className="form-field full-width">
            <label className="field-label">Station ID (idTag) *</label>
            <input
              type="text"
              className="field-input"
              placeholder="e.g., UA-ST-001"
              value={formData.idTag}
              onChange={handleIdTagChange}
              required
            />
            <span className="field-hint">Unique identifier for the station</span>
          </div>
        )}

        <div className="form-field full-width">
          <label className="field-label">Connector Types *</label>
          <div className="multi-select-wrapper" ref={dropdownRef}>
            <button
              type="button"
              className="multi-select-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className="multi-select-value">
                {formData.connectorTypes.length > 0
                  ? formData.connectorTypes.join(', ')
                  : 'Select connector types...'}
              </span>
              <span className="material-symbols-outlined multi-select-icon">
                {isDropdownOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {isDropdownOpen && (
              <div className="multi-select-dropdown">
                {CONNECTOR_OPTIONS.map((connector) => (
                  <label key={connector} className="multi-select-option">
                    <input
                      type="checkbox"
                      checked={formData.connectorTypes.includes(connector)}
                      onChange={() => handleConnectorToggle(connector)}
                    />
                    <span className="option-checkbox">
                      <span className="material-symbols-outlined">
                        {formData.connectorTypes.includes(connector) ? 'check_box' : 'check_box_outline_blank'}
                      </span>
                    </span>
                    <span className="option-label">{connector}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <span className="field-hint">Select all supported connector types</span>
        </div>

        <div className="form-field">
          <label className="field-label">Price per kWh (₴) *</label>
          <div className="field-input-wrapper">
            <NumberField
              className="field-input"
              step="1"
              min={0}
              value={formData.price}
              onChange={handlePriceChange}
              required
            />
            <span className="field-suffix">UAH</span>
          </div>
        </div>

        <div className="form-field">
          <label className="field-label">Power (kW) *</label>
          <div className="field-input-wrapper">
            <NumberField
              className="field-input"
              placeholder="e.g., 22"
              min={1}
              integer
              value={formData.option}
              onChange={handleOptionChange}
              required
            />
            <span className="field-suffix">kW</span>
          </div>
        </div>

        <div className="form-field">
          <label className="field-label">Current Limit (A)</label>
          <div className="field-input-wrapper">
            <NumberField
              className="field-input"
              placeholder="e.g., 32"
              min={6}
              max={200}
              step="1"
              allowEmpty
              value={formData.currentLimit}
              onChange={handleCurrentLimitChange}
            />
            <span className="field-suffix">A</span>
          </div>
          <span className="field-hint">Max total amperage across all connectors</span>
        </div>

        <div className="form-field">
          <label className="field-label">Load Balancing Strategy</label>
          <select
            className="field-input"
            value={formData.loadBalancingStrategy}
            onChange={handleLoadBalancingStrategyChange}
          >
            <option value={LoadBalancingStrategyEnum.None}>None</option>
            <option value={LoadBalancingStrategyEnum.BalancingBeforeSession}>Before session</option>
            <option value={LoadBalancingStrategyEnum.BalancingDuringSession}>During session</option>
          </select>
          <span className="field-hint">How to distribute capacity between connectors</span>
        </div>

        <div className="form-field">
          <label className="field-label">Downtime price (₴/min)</label>
          <div className="field-input-wrapper">
            <NumberField
              className="field-input"
              step="0.05"
              min={0}
              placeholder="e.g., 0.25"
              value={formData.downtimePricePerMinute}
              onChange={handleDowntimePriceChange}
            />
            <span className="field-suffix">UAH</span>
          </div>
          <span className="field-hint">Charged after the free window if the car stays plugged in</span>
        </div>

        <div className="form-field">
          <label className="field-label">Free downtime (min)</label>
          <div className="field-input-wrapper">
            <NumberField
              className="field-input"
              step="1"
              min={0}
              integer
              placeholder="e.g., 60"
              value={formData.downtimeFreeMinutes}
              onChange={handleDowntimeFreeChange}
            />
            <span className="field-suffix">min</span>
          </div>
          <span className="field-hint">Free minutes after charging finishes before downtime is billed</span>
        </div>

        {showOrganizationSelect && (
          <div className="form-field">
            <label className="field-label">{t('dashboard.createStationV2.organizationLabel')}</label>
            <OrganizationSelect
              value={formData.organizationId}
              onChange={(id) => onFieldChange('organizationId', id)}
            />
            <span className="field-hint">{t('dashboard.createStationV2.organizationHint')}</span>
          </div>
        )}
      </div>
    </section>
  );
});

export default TechnicalSpecsSection;
